import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Textarea } from "@shared/ui/textarea";
import {
  Search,
  User,
  Phone,
  MapPin,
  ScrollText,
  PhoneCall,
  Minimize2,
  Maximize2,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  useCallPurpose,
  useCallStatus,
  useConsumerSearchWithExtraInfo,
  useCreateCallEntry,
} from "../../hooks";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
interface ConsumerSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCallStarted: () => void;
}

export function ConsumerSelectionDialog({
  open,
  onOpenChange,
  onCallStarted,
}: ConsumerSelectionDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState<any | null>(null);
  const [callPurpose, setCallPurpose] = useState<any>("");
  const [isMinimized, setIsMinimized] = useState(false);

  // Call entry form states
  const [callNotes, setCallNotes] = useState("");

  const { data: consumers } = useConsumerSearchWithExtraInfo({
    remoteUtilityId: getRemoteUtilityId(),
    searchData: searchTerm,
    isExtraInfo: 1,
  });
  const { data: callPurposeData } = useCallPurpose();
  const { data: callStatusData } = useCallStatus();
  const createCallEntry = useCreateCallEntry();

  const getCallStatusOptions = useMemo(() => {
    if (!callStatusData || !Array.isArray(callStatusData)) {
      return [];
    }
    return callStatusData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [callStatusData]);

  const callEntryFormFields: FormField[] = [
    {
      name: "callStatus",
      type: "select",
      label: "Call Status",
      placeholder: "Select call status",
      required: true,
      options: getCallStatusOptions,
      className: "col-span-2 mt-3",
    },
    {
      name: "callDate",
      type: "datepicker",
      label: "Call Date",
      placeholder: "Select call date",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "callTime",
      type: "time",
      label: "Call Time",
      placeholder: "Select call time",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "callNotes",
      type: "textarea",
      label: "Notes",
      placeholder: "Add any additional notes about the call...",
      rows: 4,
      className: "col-span-2 mt-3",
    },
  ];

  const [callFormData, setCallFormData] = useState({
    callStatus: "",
    callDate: "",
    callTime: "",
    callNotes: "",
  });

  const callForm = FormService.createForm({
    fields: callEntryFormFields,
    validateOnMount: false,
    defaultValues: callFormData,
  });

  // Handle field changes
  const handleCallFieldChange = (fieldName: string, value: any) => {
    setCallFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const filteredConsumers = consumers?.results;
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-success/10 text-success border-success/20";
      case "overdue":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const handleConsumerSelect = (consumer: any) => {
    setSelectedConsumer(consumer);
  };

  const handleSaveDetails = () => {
    const payloadAdd = {
      consumer: selectedConsumer?.id,
      call_purpose: callPurpose,
      call_status: callFormData.callStatus,
      call_date: callFormData.callDate,
      call_time: callFormData.callTime,
      call_notes: callFormData.callNotes,
      remote_utility_id: Number(getRemoteUtilityId()),
    };
    createCallEntry.mutateAsync(payloadAdd);
    onCallStarted();
    handleReset();
    onOpenChange(false);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedConsumer(null);
    setCallPurpose("");
    setCallFormData({
      callStatus: "",
      callDate: "",
      callTime: "",
      callNotes: "",
    });
    callForm.reset({
      callStatus: "",
      callDate: "",
      callTime: "",
      callNotes: "",
    });
    setIsMinimized(false);
  };

  const callScripts = {
    5: "Hello, I'm calling regarding your recent payment or payment arrangement. I'd like to confirm the details and ensure everything is processed correctly. Do you have your account information available?",
    1: "Thank you for contacting us. I understand you have a concern you'd like to discuss. I'm here to help resolve this matter for you. Could you please provide me with the details of your complaint?",
    0: "Hello, I'm calling regarding your service request. I want to ensure we're addressing your needs properly. Could you please confirm the nature of your service requirement?",
    3: "I'm calling regarding a disconnection notice or request on your account. I'd like to discuss the details and see how we can assist you with this matter.",
    4: "Hello, I'm calling about your reconnection request. I'm here to help facilitate the restoration of your service. Let me review your account details.",
    7: "I'm calling to help you with our self-service options that can make managing your account more convenient. Would you like me to walk you through these features?",
    2: "I'm calling regarding a transfer request on your account. I want to ensure we have all the necessary information to process this smoothly.",
    6: "Hello, I'm calling regarding outstanding amounts on your account. I'd like to discuss payment options and help resolve this matter with you.",
  };

  const callScriptFor = {
    5: "Payments related",
    1: "Complaint related",
    0: "Services related",
    3: "Disconnection related",
    4: "Reconnection related",
    7: "Self service related",
    2: "Transfer related",
    6: "Outstanding related",
  };

  if (isMinimized && open) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-80">
        <Card className="shadow-lg border-2 border-primary/20 bg-background/95 backdrop-blur-sm">
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <PhoneCall className="h-4 w-4 text-primary" />
                <div className="text-sm">
                  <span className="font-medium">New Call Setup</span>
                  {selectedConsumer && (
                    <span className="text-muted-foreground ml-2">
                      - {selectedConsumer.name}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsMinimized(false)}
              >
                <Maximize2 className="h-3 w-3" />
              </Button>
            </div>

            {callPurposeData && (
              <div className="mb-3">
                <Badge variant="secondary" className="text-xs">
                  {callPurpose}
                </Badge>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="minimized-notes" className="text-xs">
                Quick Notes
              </Label>
              <Textarea
                id="minimized-notes"
                placeholder="Add notes about the call..."
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={3}
                className="text-xs resize-none"
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Dialog
      open={open && !isMinimized}
      onOpenChange={(open) => {
        if (!open) handleReset();
        onOpenChange(open);
      }}
    >
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="h-5 w-5" />
              Start New Call
              {selectedConsumer?.id && (
                <span className="text-sm font-normal text-muted-foreground">
                  -{" "}
                  {selectedConsumer?.firstName +
                    " " +
                    selectedConsumer?.lastName}
                </span>
              )}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(true)}
              className="h-8 w-8 p-0"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Consumer Search */}
        {!selectedConsumer?.id && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Consumer</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Enter name, account number, or phone number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Search Results ({filteredConsumers?.length || 0} found)
              </Label>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredConsumers?.map((consumer: any) => (
                  <Card
                    key={consumer?.id}
                    className="cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleConsumerSelect(consumer)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium">
                                {consumer?.firstName + " " + consumer?.lastName}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {consumer?.consumerNo}
                              </p>
                            </div>
                            <Badge
                              className={getStatusColor(
                                consumer?.statusDisplay
                              )}
                            >
                              {consumer?.statusDisplay}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3 w-3" />
                              <span>
                                {consumer?.territoryData?.service?.area},{" "}
                                {consumer?.territoryData?.service?.subArea},{" "}
                                {consumer?.territoryData?.service?.premise}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              <span>{consumer?.contactNumber}</span>
                            </div>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">
                              Balance:{" "}
                            </span>
                            <span
                              className={`font-medium ${
                                consumer?.outstandingBalance === "$0.00"
                                  ? "text-success"
                                  : "text-foreground"
                              }`}
                            >
                              {consumer?.outstandingBalance}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {searchTerm && filteredConsumers?.length === 0 && (
              <div className="text-center py-8">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  No consumers found matching your search
                </p>
              </div>
            )}
          </div>
        )}

        {/* Call Details with Script and Entry Form */}
        {selectedConsumer?.id && (
          <div className="space-y-4">
            <Card className="p-4 bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">
                    {selectedConsumer?.firstName +
                      " " +
                      selectedConsumer?.lastName}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedConsumer?.consumerNo}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedConsumer?.contactNumber}
                  </p>
                </div>
                <Badge
                  className={getStatusColor(selectedConsumer?.statusDisplay)}
                >
                  {selectedConsumer?.statusDisplay}
                </Badge>
              </div>
            </Card>

            <div className="space-y-2">
              <Label htmlFor="purpose">Select Call Purpose</Label>
              <Select value={callPurpose} onValueChange={setCallPurpose}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose the purpose of this call" />
                </SelectTrigger>
                <SelectContent>
                  {callPurposeData?.map((purpose) => (
                    <SelectItem key={purpose.key} value={purpose.key}>
                      {purpose.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {callPurpose !== "" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <ScrollText className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Call Script</h3>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-primary">
                        Script for: {callScriptFor[Number(callPurpose)]}
                      </div>
                      <p className="text-sm leading-relaxed">
                        {callScripts[Number(callPurpose)]}
                      </p>
                    </div>
                  </div>

                  <div className="bg-accent/50 p-3 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">Quick Tips:</h4>
                    <ul className="text-xs space-y-1 text-muted-foreground">
                      <li>• Listen actively to customer concerns</li>
                      <li>• Take detailed notes during the call</li>
                      <li>• Confirm all information before ending</li>
                      <li>• Offer additional assistance if needed</li>
                    </ul>
                  </div>
                </div>
                {/* Right Column - Call Entry Form */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Phone className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">
                      Call Entry Details
                    </h3>
                  </div>

                  <DynamicForm
                    fields={callEntryFormFields}
                    form={callForm}
                    onFieldChange={handleCallFieldChange}
                    gridColumns={2}
                    onSubmit={handleSaveDetails}
                    onReset={handleReset}
                    responsiveColumns={{
                      sm: 1,
                      md: 2,
                    }}
                    config={{
                      showResetButton: true,
                      showSubmitButton: true,
                      resetButtonText: "Cancel",
                      submitButtonText: "Save Details",
                      spacing: "comfortable",
                      classes: {
                        form: "space-y-4",
                        input:
                          "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                        textarea:
                          "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                        select:
                          "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                        label: "block text-sm font-medium text-gray-700 mb-1",
                        error: "text-sm text-red-600 mt-1",
                        container: "space-y-1",
                        buttonContainer: "flex justify-end gap-2",
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
